import React, { Component } from 'react';
import _ from 'lodash';
import {
    getChargebacks as fetchChargebacks,
    goToChargeback,
    fetchUrl
} from './chargebacksActions';
import {
    getChargebacks,
    getCount,
    getPages,
    getChargebacksErrorMessage,
    getIsChargebacksLoading
} from './chargebacksEntity';
import ChargebacksListItem from './ChargebacksListItem/ChargebacksListItem';
import ColorDollarAmountCell from '../Table/Cells/ColorDollarAmountCell/ColorDollarAmountCell';
import AmountFilter from '../Table/Filters/AmountFilter/AmountFilter';
import CardTypesFilter from '../Table/Filters/CardTypesFilter/CardTypesFilter';
import TypesFilter from '../Table/Filters/TypesFilter/TypesFilter';
import ChargebacksSummary from './ChargebacksSummary/ChargebacksSummary';
import { transformDateCell, transformDateCellNoTimeZone } from '../../utils/cell';
import Table from '../Table/Table';
import DisputedFilter from '../Table/Filters/DisputedFilter/DisputedFilter';
import CurrencyCodeFilter from '../Table/Filters/CurrencyCodeFilter/CurrencyCodeFilter';
import { dataTransform, doesPropExistAndHasItChanged, getCurrencyCode, chargebackCycleTypesMapper, getDisputeByDate } from '@nexio/emvio-util-app';
import { prettifyDate } from '../../utils/string';
import PropTypes from 'prop-types';
import { getSelectedMerchantIdsString } from '../MerchantSelector/merchantSelectorViewState';
import { getColumnsByUniqueTableName } from '../Table/tableEntity';
import { getShouldDoLargeExport } from '../CSVs/csvEntity';
import { getMerchantsMapper } from '../Merchants/merchantsEntity';
import { startLargeExport } from '../Exports/exportsActions';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { doesRoleHaveAccessToFeature } from '../../utils/customRole';
import { getCustomAccess } from '../Self/selfEntity';
import { getSelf } from '../Self/selfEntity';
import config from '../../config/config';
import InPageMerchantSelector from '../MerchantSelector/InPageMerchantSelector';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

const toolTipContent = <div>Tip: Begin search with ... to search<br />the last four. Example: ...1234</div>;

export class Chargebacks extends Component {
    dateKey = 'reportDate';

    static propTypes = {
        selectedMerchantIds: PropTypes.string,
        columns: PropTypes.array,
        merchantMap: PropTypes.any,
        kickOffLargeExport: PropTypes.func,
        shouldDoLargeExport: PropTypes.bool,
        timezone: PropTypes.string,
        customAccess: PropTypes.any,
        self: PropTypes.any
    };

    constructor(props) {
        super(props);

        this.state = {
            isExportMessageVisible: false,
            exportType: 'csv'
        };

        this.defaultColumnsAndProps = [
            {
                accessor: 'reportDate',
                Cell: transformDateCellNoTimeZone,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone',
                className: ['cursor-pointer'],
                defaultSortDesc: true,
                filterable: false,
                label: 'Date Posted',
                minWidth: 135,
                show: true
            },
            {
                accessor: 'merchantName',
                className: ['cursor-pointer'],
                filterable: false,
                label: 'Merchant',
                minWidth: 150,
                show: true,
                sortable: false
            },
            {
                accessor: 'sequenceNumber',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Case Num',
                minWidth: 155,
                show: true
            },
            {
                accessor: 'cardNumber',
                transformData: dataTransform.ellipsisNumber,
                transformType: 'ellipsisNumber',
                className: ['cursor-pointer'],
                label: 'Card Num',
                title: toolTipContent,
                minWidth: 130,
                show: true
            },
            {
                accessor: 'cardType',
                transformData: dataTransform.cardTypesMapper,
                transformType: 'cardTypesMapper',
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<CardTypesFilter filter={filter} onChange={onChange} />),
                label: 'Card Type',
                minWidth: 120,
                show: true
            },
            {
                accessor: 'amount',
                Cell: (props) => <ColorDollarAmountCell amount={props.value} currencyCode={getCurrencyCode(props.original.currencyId)} />,
                className: ['cursor-pointer', 'right-justify'],
                Filter: ({ filter, onChange }) => (<AmountFilter filter={filter} onChange={onChange} />),
                label: 'Amount',
                minWidth: 120,
                show: true
            },
            {
                accessor: 'currencyId',
                transformData: dataTransform.currencyCode,
                transformType: 'currencyCode',
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<CurrencyCodeFilter filter={filter} onChange={onChange} />),
                label: 'Currency',
                minWidth: 125,
                show: false
            },
            {
                accessor: 'authCode',
                className: ['cursor-pointer'],
                excelStringField: true,
                label: 'Auth Code',
                minWidth: 120,
                show: true
            },
            {
                accessor: 'transactionDate',
                Cell: transformDateCell,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone',
                className: ['cursor-pointer'],
                filterable: false,
                label: 'Transaction Date',
                minWidth: 180,
                show: true
            },
            {
                accessor: 'transactionDate $AS timezone',
                Cell: (transactionDate) => prettifyDate(transactionDate, { dateTimeFormatOutput: 'z' }),
                className: ['cursor-pointer'],
                defaultSortDesc: false,
                filterable: false,
                label: 'Timezone',
                minWidth: 125,
                show: false,
                transformData: dataTransform.dateGetTimezone,
                transformType: 'dateGetTimezone'
            },
            {
                accessor: 'chargebackCycle',
                transformData: (data) => (`${data.original.chargebackCycle}: ${_.toUpper(chargebackCycleTypesMapper[data.original.chargebackCycle])}`),
                className: ['cursor-pointer'],
                Filter: ({ filter, onChange }) => (<TypesFilter filter={filter} onChange={onChange} />),
                label: 'Cycle Type',
                minWidth: 140,
                show: true
            },
            {
                accessor: 'fundsMoved',
                transformData: (data) => `${data.original.fundsMoved ? 'True' : 'False'}`,
                className: ['cursor-pointer'],
                filterable: false,
                label: 'Funds Moved',
                minWidth: 180,
                show: true
            },
            {
                accessor: 'isDisputed',
                className: ['cursor-pointer'],
                transformData: dataTransform.isDisputed,
                transformType: 'isDisputed',
                Filter: ({ filter, onChange }) => (<DisputedFilter filter={filter} onChange={onChange} />),
                label: 'Dispute Status',
                minWidth: 155,
                show: true
            },
            {
                accessor: 'merchantId',
                className: ['cursor-pointer'],
                excelStringField: true,
                filterable: false,
                label: 'Merchant ID',
                minWidth: 145,
                show: false
            },
            {
                accessor: 'achDetailsId',
                className: ['cursor-pointer'],
                label: 'Deposit Detail ID',
                minWidth: 180,
                show: true
            },
            {
                accessor: 'disputes.createdAt',
                Cell: transformDateCell,
                className: ['cursor-pointer'],
                filterable: false,
                sortable: false,
                label: 'Date Disputed',
                minWidth: 135,
                show: true
            },
            {
                accessor: 'processor.processorType',
                transformData: ({ original = {} }) => getDisputeByDate(original.reportDate, original.chargebackCycle, _.get(original, 'processor.processorType'), this.props.timezone),
                transformType: 'disputeByDateByProcessorType',
                className: ['cursor-pointer'],
                filterable: false,
                sortable: false,
                label: 'Dispute By',
                minWidth: 135,
                show: true
            },
            {
                accessor: 'reasonCode',
                className: ['cursor-pointer'],
                transformData: dataTransform.trimStart,
                transformType: 'trimStart',
                label: 'Reason Code',
                filterable: false,
                minWidth: 135,
                show: false
            },
            {
                accessor: 'reason.description',
                className: ['cursor-pointer'],
                label: 'Reason Description',
                minWidth: 210,
                filterable: false,
                sortable: false,
                show: true
            },
            {
                accessor: 'transaction.orderNumber',
                className: ['cursor-pointer'],
                label: 'Order ID',
                minWidth: 150,
                show: true
            },
            {
                accessor: 'transaction.cardHolder',
                transformData: dataTransform.uppercaseCell,
                transformType: 'uppercaseCell',
                className: ['cursor-pointer'],
                label: 'Card Holder',
                minWidth: 200,
                show: false
            },
            {
                accessor: 'transaction.amount',
                Cell: (props) => <ColorDollarAmountCell amount={props.value} currencyCode={getCurrencyCode(props.original.currencyId)} overrideText=" " />,
                className: ['right-justify', 'cursor-pointer'],
                Filter: ({ filter, onChange }) => (<AmountFilter filter={filter} onChange={onChange} />),
                label: 'Transaction Amount',
                minWidth: 200,
                show: true
            },
            {
                accessor: 'referenceNumber',
                className: ['cursor-pointer'],
                label: 'Reference Number',
                minWidth: 225,
                show: true
            }
        ];
    }

    componentDidUpdate(previousProps) {
        if (doesPropExistAndHasItChanged(previousProps, this.props, 'shouldDoLargeExport') && this.props.shouldDoLargeExport === true) {
            const { selectedMerchantIds, columns, merchantMap, timezone } = this.props;
            const { filters, sorts, exportType } = this.state;
            this.setState({
                isExportMessageVisible: true,
                exportButtonMessage: 'The data range is too large to export now. We will send you an email with a link to the finished report.'
            });

            this.props.kickOffLargeExport(selectedMerchantIds, columns, filters, sorts, this.dateKey, 'chargeback', merchantMap, timezone, exportType);
        }
    }

    toggleExportMessageVisible = (e) => {
        e.stopPropagation();
        this.setState((prevState) => ({
            isExportMessageVisible: !prevState.isExportMessageVisible
        }));
    };

    getTableState = (tableState) => {
        this.setState({
            ...tableState
        });
    };

    setExportType = (type) => {
        this.setState({ exportType: type });
    };

    render() {
        const doesHaveAccessToSummaryData = doesRoleHaveAccessToFeature(this.props.customAccess, { [config.serviceArns.chargeback]: ['GET/v3/summary'] });
        const doesHaveAccessToChargebackData = doesRoleHaveAccessToFeature(this.props.customAccess, { [config.serviceArns.chargeback]: ['GET/v3'] });

        return (
            <>
                {this.props.flags?.resellerPhase1 && this.props.self?.accountType === 'RESELLER' && <InPageMerchantSelector />}
                <div className="emvio-chargebacks">
                    {doesHaveAccessToSummaryData ? <ChargebacksSummary /> : null}
                    {doesHaveAccessToChargebackData
                        ? <Table
                            fetchUrl={fetchUrl}
                            className="emvio-chargebacks-table"
                            dateKey={this.dateKey}
                            defaultColumnsAndProps={this.defaultColumnsAndProps}
                            defaultSorted={[{
                                desc: true,
                                id: this.dateKey
                            }]}
                            fetchData={fetchChargebacks}
                            countSelector={getCount}
                            dataSelector={getChargebacks}
                            errorMessageSelector={getChargebacksErrorMessage}
                            isDataLoadingSelector={getIsChargebacksLoading}
                            pagesSelector={getPages}
                            itemKey="id"
                            hasNoCount={true}
                            selectRow={goToChargeback}
                            ListItemComponent={ChargebacksListItem}
                            noDataText="No Chargebacks Found"
                            shouldUseGlobalDate
                            uniqueTableName="Chargebacks"
                            getTableState={this.getTableState}
                            exportButtonMessage={this.state.exportButtonMessage}
                            isExportMessageVisible={this.state.isExportMessageVisible}
                            toggleExportMessageVisible={this.toggleExportMessageVisible}
                            filterable={true}
                            setExportType={this.setExportType}
                        />
                        :
                        null
                    }
                </div>
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        selectedMerchantIds: getSelectedMerchantIdsString(state),
        columns: getColumnsByUniqueTableName(state, 'Chargebacks'),
        shouldDoLargeExport: getShouldDoLargeExport(state),
        merchantMap: getMerchantsMapper(state),
        customAccess: getCustomAccess(state),
        self: getSelf(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        kickOffLargeExport: (merchantIds, columns, filters, sorts, dateKey, table, merchantMap, timezone, exportType) => {
            dispatch(startLargeExport(merchantIds, columns, filters, sorts, dateKey, table, merchantMap, timezone, exportType));
        }
    };
}

export default withLDConsumer()(withRouter(connect(mapStateToProps, mapDispatchToProps)(Chargebacks)));
